import React, { createContext, useContext, useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { STORAGE_KEYS } from '../utils/constants';
import { uuid } from '../utils/formatting';
import { useBudgetData } from './hooks/useBudgetData';
import { useFileSync }   from './hooks/useFileSync';
import { useDriveSync }  from './hooks/useDriveSync';

const BudgetContext = createContext(null);

export function BudgetProvider({ children }) {
  // ─── Theme ────────────────────────────────────────────────────────────────────
  const [theme, setTheme] = useLocalStorage(STORAGE_KEYS.theme, 'light');
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);
  const toggleTheme = useCallback(() => setTheme(t => t === 'dark' ? 'light' : 'dark'), [setTheme]);

  // ─── Toasts ───────────────────────────────────────────────────────────────────
  const [toasts, setToasts] = useState([]);
  const showToast = useCallback((msg, type = 'success') => {
    const id = uuid();
    setToasts(prev => [...prev, { id, msg, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
  }, []);

  // ─── Mobile entry sheet (UI-only) ──────────────────────────────────────────────
  // Lifted here so the phone transaction list (Tracker) can open the same sheet the
  // bottom-nav FAB uses — for both "add" (editTxn=null) and "edit" (editTxn=txn).
  const [entrySheet, setEntrySheet] = useState({ open: false, editTxn: null });
  const openEntrySheet  = useCallback((editTxn = null) => setEntrySheet({ open: true, editTxn }), []);
  const closeEntrySheet = useCallback(() => setEntrySheet({ open: false, editTxn: null }), []);

  // ─── Core data state + domain helpers ────────────────────────────────────────
  const budgetData = useBudgetData({ showToast });

  // savePayloadRef breaks the stale-closure problem in Drive auto-save's debounced
  // setTimeout: the timer captures the ref once at scheduling time, then reads
  // .current inside the callback to always write the freshest data.
  const savePayloadRef = useRef(null);
  useEffect(() => { savePayloadRef.current = budgetData.savePayload; }, [budgetData.savePayload]);

  // Setters passed to sync hooks so they can apply restored data
  const setters = useMemo(() => ({
    setSettings:           budgetData.setSettings,
    setCategories:         budgetData.setCategories,
    setBudgetItems:        budgetData.setBudgetItems,
    setTransactions:       budgetData.setTransactions,
    setBuckets:            budgetData.setBuckets,
    setYearlyExpenses:     budgetData.setYearlyExpenses,
    setCurrencyBalances:   budgetData.setCurrencyBalances,
    setRecurringTemplates: budgetData.setRecurringTemplates,
  }), [budgetData.setSettings, budgetData.setCategories, budgetData.setBudgetItems,
       budgetData.setTransactions, budgetData.setBuckets, budgetData.setYearlyExpenses,
       budgetData.setCurrencyBalances, budgetData.setRecurringTemplates]);

  // ─── Sync hooks ───────────────────────────────────────────────────────────────
  const fileSync  = useFileSync({ savePayload: budgetData.savePayload, setters, showToast });
  const driveSync = useDriveSync({ savePayload: budgetData.savePayload, savePayloadRef, setters, showToast });

  // ─── Context value ────────────────────────────────────────────────────────────
  // useMemo prevents the context value object from being recreated on every render,
  // so consumers only re-render when something they actually use changes.
  // Note: consumers of useContext(BudgetContext) still re-render whenever *any* part
  // of value changes (React Context does not support partial subscriptions). Splitting
  // into multiple contexts (DataContext, UIContext) would be needed to fully eliminate
  // toast-driven re-renders in data-only consumers — a future improvement if needed.
  // Raw setters and savePayload live in budgetData but are internal — consumed by
  // sync hooks via setters/savePayloadRef. Components use named helpers instead.
  // The destructuring happens inside useMemo so it only runs when budgetData changes.
  const value = useMemo(() => {
    const {
      savePayload: _sp,
      setSettings, setCategories, setBudgetItems, setTransactions,
      setBuckets, setYearlyExpenses, setCurrencyBalances, setRecurringTemplates,
      ...publicData
    } = budgetData;
    return {
      ...publicData, ...fileSync, ...driveSync, theme, toggleTheme, toasts, showToast,
      entrySheet, openEntrySheet, closeEntrySheet,
    };
  }, [budgetData, fileSync, driveSync, theme, toggleTheme, toasts, showToast,
      entrySheet, openEntrySheet, closeEntrySheet]);

  return <BudgetContext.Provider value={value}>{children}</BudgetContext.Provider>;
}

export function useBudget() {
  const ctx = useContext(BudgetContext);
  if (!ctx) throw new Error('useBudget must be used within BudgetProvider');
  return ctx;
}
